import { AfterContentChecked, AfterContentInit, Component, EventEmitter, HostListener, Input, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { KeyValueObject } from "../../../../models/core/KeyValueObject";
import { WorkflowTransition } from "../../../../models/request/workflow-transition";
import { ProjectWorkflowTransitionType } from "../../../../models/request/workflow-transition-type";
import { ChangeStatusModel } from "../../../../models/change-status-models/change-status-model";
import { RequestBwt } from "../../../../models/request/request-bwt";
import { SetPerformerUserGroup } from "../../../workflow-transitions/set-performer-user-group/set-performer-user-group";
import { SetPerformerUserGroupModel } from "../../../workflow-transitions/set-performer-user-group/set-performer.model";
import { PerformerUserGroupModel } from "../../../../models/change-status-models/performer-user-group-model";
import { DialogRef, DialogResult, DialogService } from "@progress/kendo-angular-dialog";
import { CommentKindEnum } from "../../../../enums/comment-kind.enum";
import { SetReasonForStopping } from "../../../workflow-transitions/set-reason-for-stopping/set-reason-for-stopping";
import { RequestsService } from "../../../../services/requests.service";
import { StopRequestModel } from "../../../../models/change-status-models/stop-request-model";
import { UserGroupsService } from "../../../../services/user-groups.service";
import { UserGroup } from "../../../../models/user-group/user-group";
import { SetProblemResolutionComponent } from "../../../workflow-transitions/set-problem-resolution/set-problem-resolution.component";
import { tap } from "rxjs/operators";
import { CreateRequestWaterSamplingComponent } from "../../../workflow-transitions/create-request-water-sampling/create-request-water-sampling.component";
import { CreateRequestWaterSamplingActComponent } from "../../../workflow-transitions/create-request-water-sampling-act/create-request-water-sampling-act.component";
import { CreateLaboratoryExaminationCertificateComponent } from "../../../workflow-transitions/create-laboratory-examination-certificate/create-laboratory-examination-certificate.component";
import { RequestChangeStatusResult } from "../../../../models/request/request-change-status-result";
import { LaboratoryExaminationCertificatesService } from "../../../../services/laboratory-examination-certificates.service";
import { LaboratoryExaminationCertificateDto } from "../../../../models/laboratory/laboratory-examination-certificate.dto";
import { LaboratoryWaterSamplesAdditionalComponent } from "../../../workflow-transitions/laboratory-water-samples-additional/laboratory-water-samples-additional.component";
import { SetCarrier } from "../../../workflow-transitions/set-carrier/set-carrier";
import { SettingsService } from "../../../../services/settings.service";
import { forkJoin } from "rxjs";
import { EngineerSettingsDto } from "../../../../models/settings/engineer-settings";
import { SetPerformerEngineerUserGroup } from "../../../workflow-transitions/set-performer-engineer-user-group/set-performer-engineer-user-group";
import { TransitionConfirmationRequestComponent } from "../../../workflow-transitions/transition-confirmation-request/transition-confirmation-request.component";
import { SetCommentModel } from "../../../workflow-transitions/set-comment/set-comment.model";
import { CommentModel } from "../../../../models/change-status-models/comment-model";
import { NotificationService } from "../../../../../core/services/notification.service";
import { SecurityService } from "../../../../../core/services/security.service";
import { RequestStatus } from "../../../../enums";

@Component({
	selector: 'request-status-workflow-transitions',
	templateUrl: './request-status-workflow-transitions.component.html',
	styleUrls: ['./request-status-workflow-transitions.component.scss',
		'../../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
		'../../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
	],
	encapsulation: ViewEncapsulation.None,
})
export class RequestStatusWorkflowTransitionsComponent implements AfterContentInit {

    @Input() request: RequestBwt = null;
    @Input() workflowTransitions: KeyValueObject[] = [];
	@Input() autoCallingWorkflowTransitionId: number;
	@Input() disabled: boolean = false;

    @Output() beforeChangeStatusEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() afterChangeStatusEvent: EventEmitter<RequestChangeStatusResult> = new EventEmitter<RequestChangeStatusResult>();
	@Output() cancelChangeStatusEvent: EventEmitter<any> = new EventEmitter<any>();

	requestStatusId: number;
	modalUserGroups: UserGroup[] = [];
	engineerSettings: EngineerSettingsDto;

    constructor (
        private dialogService: DialogService,
        private requestsService: RequestsService,
        private userGroupsService: UserGroupsService,
		private settingsService: SettingsService,
		private notificationService: NotificationService,
		private securityService: SecurityService
    ) { }

	ngAfterContentInit(): void {
		const engineerSettings$ = this.settingsService.getEngineerSettings();
		const userGroups$ = this.userGroupsService.getAll(this.request.requestId);

		forkJoin([engineerSettings$, userGroups$]).subscribe(([
			engineerSettings, userGroups
		]) => {
			this.engineerSettings = engineerSettings;
			this.modalUserGroups = userGroups;
			
			this.requestStatusId = this.request.requestStatusId;
			if (this.autoCallingWorkflowTransitionId) {
				this.changeStatus(this.autoCallingWorkflowTransitionId);
			}

		});
	}

	changeStatus(transitionId: number) {
		if (this.isFullReadonly) {
			this.delayedReturnRequestStatus();
			return;
		}

		let transition = this.request.workflowTransitions.find(x => x.transitionId == transitionId);
		this.delayedReturnRequestStatus();

		if (transition == null) {
			return;
		}

		if (this.engineerSettings.planningRequestStatusIds.some(x => x == transition.requestStatusToId)) {
			this.showEngineerPlanningModal(transition);
			return;
		}

		switch (transition.projectWorkflowTransitionType) {
			case ProjectWorkflowTransitionType.SetPerformerUserAndGroupTransition:
				this.showSetPerformerUserAndGroupTransition(transition);
				break;

			case ProjectWorkflowTransitionType.SetReasonForStopping: 
				this.showSetReasonForStopping(transition);
				break;

			case ProjectWorkflowTransitionType.transitionConfirmationRequest:
				this.transitionConfirmationRequest(transition);
				break;

            case ProjectWorkflowTransitionType.planned:
                this.planned(transition);
                break;
			
			case ProjectWorkflowTransitionType.completedReclamation: 
				this.completedReclamation(transition);
				break;

			case ProjectWorkflowTransitionType.fillSampling:
				this.fillSampling(transition);
				break;

			case ProjectWorkflowTransitionType.fillSamplingAct: 
				this.fillSamplingAct(transition);
				break;

			case ProjectWorkflowTransitionType.selectPapeworkOptions:
				this.selectPapeworkOptions(transition);
				break;

			case ProjectWorkflowTransitionType.transferLaboratoryWaterSamples:
				this.transferLaboratoryWaterSamples(transition);
				break;

			case ProjectWorkflowTransitionType.choosingTransportCompanyAndSpecifyingTrackNumber:
				this.choosingTransportCompanyAndSpecifyingTrackNumber(transition);
				break;

			default: 	
				this.changeStatusCore(transition, new ChangeStatusModel(this.request.requestId, transition.requestStatusToId));
				break;
		}
	}

	showEngineerPlanningModal(transition: WorkflowTransition) {
		this.requestsService.checkRequestCategoryDevices(this.request.requestId, transition.transitionId).subscribe(result => {
			if (result.requireCategoryDevice == true && result.hasCategoryDevice == false) {
				this.notificationService.confirmation({
					message: 'Для назначения визита мастера необходимо указать какое оборудование должно быть установлено или обслужено на объекте',
					title: 'Не указано оборудование',
					confirmButtonText: 'Ок',
					showConfirmButton: true,
					showCancelButton: false,
				});
			}
			else {
				const dialogRef = this.dialogService.open({
					content: SetPerformerEngineerUserGroup,
					width: '50%',
					title: `Переход в статус '${transition.transitionName}'`
				});
			
				const dialog = <SetPerformerEngineerUserGroup>dialogRef.content.instance;

				dialog.requestId = this.request.requestId;
				dialog.requestIntegraCode = this.request.integraCode;
				dialog.setPerformerUserGroupModel = new SetPerformerUserGroupModel(
					transition.defaultPerformUserGroupId, 
					this.request.performerUserGroupId == transition.defaultPerformUserGroupId
						? this.request.performerUserId
						: null
				);
				dialog.setPerformerUserGroupModel.commentKind = CommentKindEnum.internal;
				dialog.workflowTransition = transition;
				dialog.requestId = this.request.requestId;
				dialog.requestContragentId = this.request.customerContragentId;
				dialog.requestServiceCenterId = this.request.serviceCenterId;
				dialog.requestServiceCenterIntegraCode = this.request.serviceCenterIntegraCode;
				dialog.disablePerformerSelection = this.securityService.canPlanVisitWithoutPerformerSelection();

				dialog.onContinueEvent.subscribe((result: SetPerformerUserGroupModel) => {
					this.request.performerUserGroupId = result.userGroupId;
					this.request.performerUserId = result.userId;

					this.changeStatusCore(transition, new PerformerUserGroupModel(this.request.requestId, transition.requestStatusToId, this.request.performerUserId, this.request.performerUserGroupId, result.comment, result.commentKind, result.planStartDate));
				});
				dialog.onCancelEvent.subscribe(() => {
					dialogRef.close();
				});

			}
		});
	}

	get showVisitMasterButton(): boolean {
		return this.securityService.isCallCenterEmployee() && !!this.visitMasterTransition;
	}

	onVisitMasterClick(): void {
		if (this.isFullReadonly) {
			return;
		}

		if (this.visitMasterTransition) {
			this.changeStatus(this.visitMasterTransition.transitionId);
		}
	}

	private get isFullReadonly(): boolean {
		return this.securityService.isSpecialRightClaimValid('FullReadonly');
	}

	private get visitMasterTransition(): WorkflowTransition {
		if (!this.request || !this.request.workflowTransitions) {
			return null;
		}

		let transitionsNames = ["Визит мастера", "На монтаж", "Визит специалиста"]

		return this.request.workflowTransitions.find(x =>
			x.requestStatusFromId === this.request.requestStatusId &&
			transitionsNames.includes(x.transitionName) &&
			x.projectWorkflowTransitionType === ProjectWorkflowTransitionType.planned);
	}

	showSetPerformerUserAndGroupTransition(transition: WorkflowTransition) {
		const dialogRef1 = this.dialogService.open({
			title: `Переход в статус '${transition.transitionName}'`,
			content: SetPerformerUserGroup,
			width: 400,
		});

		const setPerformerUserGroupForm = <SetPerformerUserGroup>dialogRef1.content.instance;
		setPerformerUserGroupForm.setPerformerUserGroupModel = new SetPerformerUserGroupModel(
			transition.defaultPerformUserGroupId, 
			this.request.performerUserGroupId == transition.defaultPerformUserGroupId
				? this.request.performerUserId
				: null
		);
		setPerformerUserGroupForm.setPerformerUserGroupModel.commentKind = CommentKindEnum.internal;
		setPerformerUserGroupForm.workflowTransition = transition;
		setPerformerUserGroupForm.requestId = this.request.requestId;
		setPerformerUserGroupForm.requestContragentId = this.request.customerContragentId;
		setPerformerUserGroupForm.requestServiceCenterId = this.request.serviceCenterId;

		setPerformerUserGroupForm.onCancelEvent.subscribe(() => {
			this.cancelChangeStatusCore();
		});
		setPerformerUserGroupForm.onContinueEvent.subscribe((result: SetPerformerUserGroupModel) => {
			this.request.performerUserGroupId = result.userGroupId;
			this.request.performerUserId = result.userId;

			this.changeStatusCore(transition, new PerformerUserGroupModel(this.request.requestId, transition.requestStatusToId, this.request.performerUserId, this.request.performerUserGroupId, result.comment, result.commentKind));
		});
	}

	showSetReasonForStopping(transition: WorkflowTransition) {
		const dialogRef = this.dialogService.open({
			title: `Переход в статус '${transition.transitionName}'`,
			content: SetReasonForStopping,
			width: 400
		});

		const setStopReasonAndDateForm = <SetReasonForStopping>dialogRef.content.instance;
		setStopReasonAndDateForm.setPerformerUserGroupModel = new SetPerformerUserGroupModel(
			transition.defaultPerformUserGroupId, 
			this.request.performerUserGroupId == transition.defaultPerformUserGroupId
				? this.request.performerUserId
				: null
		);		
		setStopReasonAndDateForm.setPerformerUserGroupModel.commentKind = CommentKindEnum.internal;
		setStopReasonAndDateForm.userGroups = this.modalUserGroups;
		setStopReasonAndDateForm.requestId = this.request.requestId;
		setStopReasonAndDateForm.requestContragentId = this.request.customerContragentId;

		setStopReasonAndDateForm.workflowTransition = transition;
		setStopReasonAndDateForm.stoppingReasons = [];
		setStopReasonAndDateForm.isClientAlfaBank = false;

		setStopReasonAndDateForm.onCancelEvent.subscribe(() => {
			this.cancelChangeStatusCore();
		});
		setStopReasonAndDateForm.onContinueEvent.subscribe((result: SetPerformerUserGroupModel) => {
			this.request.performerUserGroupId = result.userGroupId;
			this.request.performerUserId = result.userId;
			result.transferInitiatorId = setStopReasonAndDateForm.transferInitiatorId;
			if (setStopReasonAndDateForm.stoppingReason) {
				result.requestStoppingReasonId = setStopReasonAndDateForm.stoppingReasons.find((v, i, a) => v.id === setStopReasonAndDateForm.stoppingReason).id;
				result.comment = result.comment ? 'Комментарий: ' + result.comment : null;
			}

			if (!!setStopReasonAndDateForm.stoppedUntil) {
				setStopReasonAndDateForm.stoppedUntil.setHours(setStopReasonAndDateForm.stoppedUntil.getHours() - this.request.utcTimezoneShift);
			}

			this.changeStatusCore(transition, new StopRequestModel(
				this.request.requestId,
				transition.requestStatusToId,
				this.request.performerUserId,
				this.request.performerUserGroupId,
				result.comment,
				setStopReasonAndDateForm.stoppedUntil,
				result.commentKind,
				result.requestStoppingReasonId,
				result.transferInitiatorId));
		});
	}

	transitionConfirmationRequest(transition: WorkflowTransition) {
		const transitionConfirmationRequestRef = this.dialogService.open({
			title: `Переход в статус '${transition.transitionName}'`,
			content: TransitionConfirmationRequestComponent,
			width: 700
		});

		const transitionConfirmationRequestDialog = <TransitionConfirmationRequestComponent>transitionConfirmationRequestRef.content.instance;

		transitionConfirmationRequestDialog.setCommentModel = new SetCommentModel('');
		transitionConfirmationRequestDialog.setCommentModel.commentKind = CommentKindEnum.internal;

		transitionConfirmationRequestDialog.workflowTransition = transition;

		transitionConfirmationRequestDialog.onContinueEvent.subscribe((result: SetCommentModel) => {
			this.changeStatusCore(transition, new CommentModel(this.request.requestId, transition.requestStatusToId, result.commentText, result.commentKind));
		});
	}

    planned(transition: WorkflowTransition) {
        const dialogRef1 = this.dialogService.open({
			title: `Переход в статус '${transition.transitionName}'`,
			content: SetPerformerUserGroup,
			width: 400,
		});
		
		const setPerformerUserGroupForm = <SetPerformerUserGroup>dialogRef1.content.instance;
		setPerformerUserGroupForm.setPerformerUserGroupModel = new SetPerformerUserGroupModel(
			transition.defaultPerformUserGroupId, 
			this.request.performerUserGroupId == transition.defaultPerformUserGroupId
				? this.request.performerUserId
				: null
		);			
		setPerformerUserGroupForm.setPerformerUserGroupModel.commentKind = CommentKindEnum.internal;
		setPerformerUserGroupForm.workflowTransition = transition;
		setPerformerUserGroupForm.requestId = this.request.requestId;
        setPerformerUserGroupForm.showPlanStartDate = true;
		setPerformerUserGroupForm.requestContragentId = this.request.customerContragentId;
		setPerformerUserGroupForm.requestServiceCenterId = this.request.serviceCenterId;

		setPerformerUserGroupForm.onCancelEvent.subscribe(() => {
			this.cancelChangeStatusCore();
		});
		setPerformerUserGroupForm.onContinueEvent.subscribe((result: SetPerformerUserGroupModel) => {
			this.request.performerUserGroupId = result.userGroupId;
			this.request.performerUserId = result.userId;

			this.changeStatusCore(transition, new PerformerUserGroupModel(
                this.request.requestId, transition.requestStatusToId, this.request.performerUserId, this.request.performerUserGroupId, result.comment, result.commentKind, result.planStartDate));
		});
    }

	completedReclamation(transition: WorkflowTransition) {	
		const dialogRef = this.dialogService.open({
			title: `Переход в статус '${transition.transitionName}'`,
			content: SetProblemResolutionComponent,
			width: 540
		});

		const dialog = <SetProblemResolutionComponent>dialogRef.content.instance;
		dialog.requestId = this.request.requestId;
		dialog.claimId = this.request.claimId;
		dialog.resolution = this.request.resolution;
		dialog.reclamationDeviceId = this.request.reclamationDeviceId;

		dialog.onCancel.subscribe(() => {
			this.cancelChangeStatusCore();
		});
		dialog.onContinue.subscribe(() => {
			this.changeStatusCore(transition, new ChangeStatusModel(this.request.requestId, transition.requestStatusToId));
		});
	}

	fillSampling(transition: WorkflowTransition) {
		const dialogRef = this.dialogService.open({
			content: CreateRequestWaterSamplingComponent,
			width: 1200,
			height: 760
		});

		const dialog = <CreateRequestWaterSamplingComponent>dialogRef.content.instance;
		dialog.contentHeight = 640;
		dialog.requestId = this.request.requestId;

		dialog.onCancelEvent.subscribe(() => {
			dialogRef.close();
		});
		dialog.onContinueEvent.subscribe(() => {
			this.changeStatusCore(transition, new ChangeStatusModel(this.request.requestId, transition.requestStatusToId));
			dialogRef.close();
		});
	}

	fillSamplingAct(transition: WorkflowTransition) {
		const dialogRef = this.dialogService.open({
			content: CreateRequestWaterSamplingActComponent,
			width: 1200,
			height: 840
		});

		const dialog = <CreateRequestWaterSamplingActComponent>dialogRef.content.instance;
		dialog.contentHeight = 720;
		dialog.requestId = this.request.requestId;
		dialog.requestCategoryId = this.request.requestCategoryId;
		dialog.contragentId = this.request.customerContragentId;
		dialog.customerObjectId = this.request.customerObjectId;

		dialog.onCancelEvent.subscribe(() => {
			dialogRef.close();
		});
		dialog.onContinueEvent.subscribe(() => {
			this.changeStatusCore(transition, new ChangeStatusModel(this.request.requestId, transition.requestStatusToId));
			dialogRef.close();
		});
	}

	selectPapeworkOptions(transition: WorkflowTransition) {
		const dialogRef = this.dialogService.open({
			title: `Переход в статус '${transition.transitionName}'`,
			content: CreateLaboratoryExaminationCertificateComponent,
			width: 600,
		});

		const dialog = <CreateLaboratoryExaminationCertificateComponent>dialogRef.content.instance;
		dialog.requestId = this.request.requestId;

		dialog.onCancelEvent.subscribe(() => {
			dialogRef.close();
		});
		dialog.onContinueEvent.subscribe((certificate: LaboratoryExaminationCertificateDto) => {
			let changeStatusModel = new ChangeStatusModel(this.request.requestId, transition.requestStatusToId);
			changeStatusModel.correctionReason = certificate.correctionReason;
			this.changeStatusCore(transition, changeStatusModel);
			dialogRef.close();
		});
	}

	transferLaboratoryWaterSamples(transition: WorkflowTransition) {
		const dialogRef = this.dialogService.open({
			title: `Переход в статус '${transition.transitionName}'`,
			content: LaboratoryWaterSamplesAdditionalComponent,
			width: 600,
		});

		const dialog = <LaboratoryWaterSamplesAdditionalComponent>dialogRef.content.instance;
		dialog.requestId = this.request.requestId;

		dialog.onCancelEvent.subscribe(() => {
			dialogRef.close();
		});
		dialog.onContinueEvent.subscribe(() => {
			this.changeStatusCore(transition, new ChangeStatusModel(this.request.requestId, transition.requestStatusToId));
			dialogRef.close();
		});
	}

	choosingTransportCompanyAndSpecifyingTrackNumber(transition: WorkflowTransition) {
		this.requestsService.getCarrierCompanyTrack(this.request.requestId).subscribe(resp => {
			if (resp != null && resp.trackNumber != null && resp.trackNumber.trim() != '') {
				this.changeStatusCore(transition, new ChangeStatusModel(this.request.requestId, transition.requestStatusToId));
			}
			else {
				const dialogRef = this.dialogService.open({
					content: SetCarrier,
					width: '30%'
				});

				const setCarrierForm = <SetCarrier>dialogRef.content.instance;

				setCarrierForm.requestId = this.request.requestId;

				setCarrierForm.onContinueEvent.subscribe(() => {
					this.changeStatusCore(transition, new ChangeStatusModel(this.request.requestId, transition.requestStatusToId));
				});
			}
		});
	}

	cancelChangeStatusCore() {
		this.delayedReturnRequestStatus();
		this.cancelChangeStatusEvent.emit();
	}

	delayedReturnRequestStatus() {
		setTimeout(() => this.requestStatusId = this.request.requestStatusId, 1);
	}

	changeStatusCore(transition: WorkflowTransition, changeStatusModel: ChangeStatusModel) {
		this.requestStatusId = transition.transitionId;
		this.beforeChangeStatusEvent.emit();

		changeStatusModel.transitionId = transition.transitionId;
		this.requestsService.changeStatus(changeStatusModel).pipe(tap(resp => {
			if (!resp.isSuccessful) {
				this.cancelChangeStatusCore();
			}},
			error => {
				this.cancelChangeStatusCore();
			}
		)).subscribe((resp) => {
            this.afterChangeStatusEvent.emit(resp.data);
			this.requestStatusId = transition.requestStatusToId;
		}, () => this.cancelChangeStatusCore());
	}

	@HostListener('document:keydown', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent) {
	  	if (event.key === 'Escape') {
			this.cancelChangeStatusCore();
	  	}
	}
}
